// Shared service icons; dialog callers can override the icon for a specific action.
const serviceIcons = {
  suppliers: 'local_shipping',
  'supplier-contacts': 'contact_page',
  'supplier-assessments': 'fact_check',
  'supplier-documents': 'attach_file',
  webhooks: 'webhook',
  'webhook-queue': 'send',
  'webhook-logs': 'history',
  customers: 'apartment',
  'customer-contacts': 'contact_page',
  'customer-users': 'manage_accounts',
  'customer-api-accounts': 'api',
  'admin-api-accounts': 'admin_panel_settings',
  'account-invitations': 'person_add',
  accountInvitations: 'person_add',
  contacts: 'contacts',
  users: 'person',
  roles: 'admin_panel_settings',
  'authmanagement-roles': 'admin_panel_settings',
  settings: 'settings',
  whitelabels: 'palette',
  products: 'inventory_2',
  'purchase-orders': 'shopping_cart',
  stock: 'warehouse',
  'stock-locations': 'location_on',
  assets: 'devices_other',
  'stock-movements': 'history',
  'product-groups': 'category',
  contracts: 'description',
  subscriptions: 'autorenew',
  invoices: 'receipt_long',
  'sales-orders': 'shopping_cart',
  'customer-offers': 'request_quote',
  'direct-debit': 'account_balance',
  sepa: 'account_balance',
  vat: 'percent',
  projects: 'work_outline',
  'project-tasks': 'task_alt',
  'project-task-types': 'category',
  'project-task-time-tracking': 'timer',
  domains: 'language',
  'domain-ns-groups': 'dns',
  dmarc: 'verified_user',
  webhosting: 'cloud',
  'webhosting-packages': 'deployed_code',
  'ssl-certificates': 'lock',
  licenses: 'key',
  'license-slas': 'support_agent',
  'license-partner-programs': 'handshake',
  'email-queue': 'outgoing_mail',
  'email-logs': 'mail',
  'email-triggers': 'notifications_active',
  'sms-logs': 'sms',
  'audit-logs': 'history',
  'vpn-certificates': 'vpn_key',
  'vpn-firewalls': 'security',
  'vpn-mac-address-groups': 'devices',
  'customer-sip-accounts': 'phone',
  'customer-phone-numbers': 'dialpad',
  'sharingcenter-calendars': 'calendar_month',
  'sharingcenter-profiles': 'account_circle',
  'sharingcenter-contacts': 'contacts',
  'sharingcenter-groups': 'groups',
  'feedback-questions': 'quiz',
  'microsoft-sign-in-monitor': 'shield',
}

export const getServiceIcon = (service) => `sym_o_${serviceIcons[service] || 'description'}`

// Service paths and route names differ for these shared overview components.
const serviceRoutes = {
  'authmanagement-roles': 'roles',
  'project-tasks': 'tasks',
  'project-task-types': 'task-types',
  'project-task-time-tracking': 'time-tracking',
  'feedback-questions': 'feedback',
  'sharingcenter-calendars': 'sharing-center-calendar-admin',
  'sharingcenter-contacts': 'sharing-center-contact-admin',
  'sharingcenter-groups': 'sharing-center-group-admin',
  'sharingcenter-profiles': 'sharing-center-profile-admin',
  webhosting: 'webhosting-accounts',
  settings: 'settings-management',
  sepa: 'direct-debit',
}

export const getServiceTitle = (service, t, te) => {
  const overviewKey = `Shared.OverviewTitles.${service}`
  const routeKey = `Shared.Globals.Routes.${serviceRoutes[service] || service}`
  return te(overviewKey) ? t(overviewKey) : te(routeKey) ? t(routeKey) : t('Shared.Globals.Matching records')
}

// Menus and dialogs share these icons; service summary cards keep their own symbols.
export const menuIcons = {
  'relationship-management': 'sym_o_apartment',
  'product-management': 'sym_o_inventory',
  logistics: 'sym_o_local_shipping',
  webhosting: 'sym_o_cloud',
  financial: 'sym_o_payments',
  'vision-insights': 'sym_o_monitoring',
  'project-management': 'sym_o_cases',
  'monitoring-assets': 'sym_o_overview',
  'vpn-management': 'sym_o_vpn_lock',
  communication: 'sym_o_chat',
  'sharing-center': 'sym_o_share',
  management: 'sym_o_settings',
  settings: 'sym_o_tune',
  profile: 'sym_o_account_circle',
  'customer-licenses': 'sym_o_workspace_premium',
  'customer-backups': 'sym_o_backup',
  'customer-webhosting': 'sym_o_dns',
  'customer-domains': 'sym_o_public',
  'customer-contacts': 'sym_o_contacts',
  'customer-users': 'sym_o_groups',
  'customer-api-accounts': 'sym_o_key',
}

const serviceMenuSections = {
  suppliers: 'relationship-management',
  'supplier-contacts': 'relationship-management',
  'supplier-assessments': 'relationship-management',
  'supplier-documents': 'relationship-management',
  customers: 'relationship-management',
  'customer-contacts': 'relationship-management',
  contracts: 'relationship-management',
  products: 'product-management',
  'purchase-orders': 'logistics',
  stock: 'logistics',
  'stock-locations': 'logistics',
  assets: 'logistics',
  'stock-movements': 'logistics',
  'product-groups': 'product-management',
  webhosting: 'webhosting',
  'webhosting-packages': 'webhosting',
  domains: 'webhosting',
  'domain-ns-groups': 'webhosting',
  'ssl-certificates': 'webhosting',
  contacts: 'webhosting',
  subscriptions: 'financial',
  'customer-offers': 'financial',
  'sales-orders': 'financial',
  licenses: 'financial',
  invoices: 'financial',
  sepa: 'financial',
  'direct-debit': 'financial',
  'license-slas': 'financial',
  'license-partner-programs': 'financial',
  projects: 'project-management',
  'project-tasks': 'project-management',
  'project-task-types': 'project-management',
  'project-task-time-tracking': 'project-management',
  'customer-assets': 'monitoring-assets',
  'microsoft-privileged-sign-ins': 'monitoring-assets',
  'microsoft-sign-in-monitor': 'monitoring-assets',
  'vpn-certificates': 'vpn-management',
  'vpn-firewalls': 'vpn-management',
  'vpn-mac-address-groups': 'vpn-management',
  'email-queue': 'communication',
  'email-logs': 'communication',
  'email-triggers': 'communication',
  'sms-logs': 'communication',
  'sharingcenter-calendars': 'sharing-center',
  'sharingcenter-contacts': 'sharing-center',
  'sharingcenter-groups': 'sharing-center',
  'sharingcenter-profiles': 'sharing-center',
  'sharingcenter-calendar-appointments': 'sharing-center',
  'sharingcenter-meeting-files': 'vision-insights',
  'customer-sip-accounts': 'sharing-center',
  'customer-phone-numbers': 'sharing-center',
  'ai-queue': 'sharing-center',
  users: 'management',
  'customer-users': 'management',
  'customer-api-accounts': 'management',
  roles: 'management',
  'authmanagement-roles': 'management',
  'account-invitations': 'management',
  vat: 'management',
  'audit-logs': 'management',
  settings: 'management',
  whitelabels: 'management',
  dmarc: 'management',
  webhooks: 'management',
  'webhook-queue': 'management',
  'webhook-logs': 'management',
  'feedback-questions': 'management',
  profile: 'profile',
}

const customerMenuSections = {
  licenses: 'customer-licenses',
  webhosting: 'customer-webhosting',
  domains: 'customer-domains',
  contacts: 'customer-contacts',
  'customer-users': 'customer-users',
  'customer-api-accounts': 'customer-api-accounts',
  users: 'profile',
  'sharingcenter-meeting-files': 'sharing-center',
}

const dialogServiceAliases = {
  accountinvitations: 'account-invitations',
  'webhosting-upgrade': 'webhosting',
  'webhosting-send-account-details': 'webhosting',
  'subscription-cancellation': 'subscriptions',
  'authmanagement-roles': 'roles',
  'direct-debit': 'sepa',
}

export const getDialogIcon = (service, portal = process.env.FRONTEND_TYPE) => {
  const normalized = service?.toLowerCase() || ''
  const key = dialogServiceAliases[normalized] || normalized
  const section = (portal === 'Customer' && customerMenuSections[key]) || serviceMenuSections[key]
  return menuIcons[section] || getServiceIcon(key)
}

export const getDialogSubtitle = (service, type, t, te) => {
  if (type === 'delete') return t('Shared.DialogSubtitles.actions.delete')
  const normalized = service?.toLowerCase() || ''
  const key = dialogServiceAliases[normalized] || normalized
  const translation = `Shared.DialogSubtitles.services.${key}`
  return te(translation) ? t(translation) : t('Shared.DialogSubtitles.actions.details')
}
